import CloseIcon from '@mui/icons-material/Close'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { useEffect, useState } from 'react'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { toast } from 'react-toastify'
import baseUrl from '../../api/baseUrl'
import {
  notifySmsBalanceChanged,
  showSmsResultToast,
} from '../../utils/sms/smsToast'
import { ModalOverlay } from '../ModalOverlay'
import BirthdayWishOptIn from '../sms/BirthdayWishOptIn'
import { type ClinicPatientOption } from './labPatientUtils'

type QuickAddForm = {
  fullName: string
  gender: string
  mobileNo: string
  dob: string
  address: string
  smsBirthdayWish: boolean
}

const emptyForm = (): QuickAddForm => ({
  fullName: '',
  gender: '',
  mobileNo: '',
  dob: '',
  address: '',
  smsBirthdayWish: false,
})

const fieldClass =
  'h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100'
const labelClass =
  'mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400'

type LabQuickAddPatientModalProps = {
  open: boolean
  onClose: () => void
  onCreated: (patient: ClinicPatientOption) => void
  initialName?: string
}

export default function LabQuickAddPatientModal({
  open,
  onClose,
  onCreated,
  initialName = '',
}: LabQuickAddPatientModalProps) {
  const [form, setForm] = useState<QuickAddForm>(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setForm({ ...emptyForm(), fullName: initialName.trim() })
    setError('')
  }, [open, initialName])

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose()
    }
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose, submitting])

  if (!open) return null

  function patch(partial: Partial<QuickAddForm>) {
    setForm((prev) => ({ ...prev, ...partial }))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.fullName.trim() || !form.gender || !form.mobileNo) {
      setError('Full name, gender, and phone are required')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const res = await baseUrl.post('patient/quick-register', {
        fullName: form.fullName.trim(),
        gender: form.gender,
        mobileNo: form.mobileNo,
        dob: form.dob || undefined,
        address: form.address.trim() || undefined,
        country: 'Sri Lanka',
        smsBirthdayWish: form.smsBirthdayWish,
      })
      if (!res.data?.success) {
        setError(res.data?.msg || 'Failed to add patient')
        return
      }
      const p = res.data.data
      toast.success('Patient added')
      showSmsResultToast(res.data.sms)
      notifySmsBalanceChanged()
      onCreated({
        _id: String(p._id),
        fullName: p.fullName,
        regNo: p.regNo,
        mobileNo: p.mobileNo,
        dob: p.dob,
        gender: p.gender,
        address: p.address,
      })
      onClose()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { msg?: string; message?: string } } })
          ?.response?.data?.msg ||
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ||
        'Failed to add patient'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalOverlay
      className="fixed inset-0 z-[220] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lab-quick-add-patient-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose()
      }}
    >
      <div
        className="relative my-4 w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800 sm:my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-700 sm:px-5">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <PersonAddIcon sx={{ fontSize: 22 }} />
            </div>
            <div>
              <h2
                id="lab-quick-add-patient-title"
                className="text-base font-semibold text-slate-900 dark:text-slate-100"
              >
                Add new patient
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Register and use on this order immediately.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex size-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>
        </header>

        <form onSubmit={submit} className="space-y-4 px-4 py-4 sm:px-5">
          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </p>
          ) : null}

          <div>
            <label htmlFor="qa-fullName" className={labelClass}>
              Full name <span className="text-red-500">*</span>
            </label>
            <input
              id="qa-fullName"
              type="text"
              value={form.fullName}
              onChange={(e) => patch({ fullName: e.target.value })}
              placeholder="Enter full name"
              className={fieldClass}
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="qa-gender" className={labelClass}>
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                id="qa-gender"
                value={form.gender}
                onChange={(e) => patch({ gender: e.target.value })}
                className={fieldClass}
                required
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="qa-dob" className={labelClass}>
                Date of birth
              </label>
              <input
                id="qa-dob"
                type="date"
                value={form.dob}
                onChange={(e) => patch({ dob: e.target.value })}
                className={fieldClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>
              Phone <span className="text-red-500">*</span>
            </label>
            <div className="[&_.PhoneInput]:flex [&_.PhoneInput]:h-10 [&_.PhoneInput]:items-center [&_.PhoneInput]:rounded-lg [&_.PhoneInput]:border [&_.PhoneInput]:border-slate-300 [&_.PhoneInput]:bg-white [&_.PhoneInput]:px-3 [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:text-sm [&_.PhoneInputInput]:outline-none dark:[&_.PhoneInput]:border-slate-600 dark:[&_.PhoneInput]:bg-slate-900 dark:[&_.PhoneInputInput]:text-slate-100">
              <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry="LK"
                value={form.mobileNo}
                onChange={(value) => patch({ mobileNo: value || '' })}
              />
            </div>
          </div>

          <div>
            <label htmlFor="qa-address" className={labelClass}>
              Address
            </label>
            <input
              id="qa-address"
              type="text"
              value={form.address}
              onChange={(e) => patch({ address: e.target.value })}
              placeholder="Optional"
              className={fieldClass}
            />
          </div>

          <BirthdayWishOptIn
            dob={form.dob}
            checked={form.smsBirthdayWish}
            onChange={(value) => patch({ smsBirthdayWish: value })}
          />

          <footer className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 dark:border-slate-700 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-gradient-to-r from-primary to-primaryLight px-5 py-2.5 text-sm font-semibold text-white shadow-sm disabled:opacity-50"
            >
              {submitting ? 'Adding…' : 'Add & use patient'}
            </button>
          </footer>
        </form>
      </div>
    </ModalOverlay>
  )
}
